import {
  AgentMetaCard,
  type AgentMetaItem,
  type AgentMetaSection,
} from "@/pages/agent/components/agent-meta-card";
import type { AgentIntentTask } from "@/pages/agent/intent-graph/agent-intent-graph.types";
import { formatAgentDate } from "@/pages/agent/utils/agent-date.utils";
import type { TaskDetailViewModel } from "@/pages/agent/task/agent-task-page.utils";

export const AgentTaskMetaCard = ({
  intent,
  taskDetail,
}: {
  intent?: AgentIntentTask;
  taskDetail: TaskDetailViewModel;
}) => {
  const { task, nextFollowUp, followUpCount, followUpsLoading } = taskDetail;

  const taskItems: AgentMetaItem[] = [
    {
      label: "Due",
      value: task.dueAt ? formatAgentDate({ value: task.dueAt, includeTime: true }) : "No due date",
    },
    {
      label: "Created",
      value: formatAgentDate({ value: task.createdAt, includeTime: true }),
    },
    {
      label: "Updated",
      value: formatAgentDate({ value: task.updatedAt, includeTime: true }),
    },
    {
      label: "Next follow-up",
      value: followUpsLoading
        ? "Loading…"
        : nextFollowUp
          ? formatAgentDate({
              value: nextFollowUp.scheduledFor ?? nextFollowUp.dueAt,
              includeTime: true,
            })
          : "None scheduled",
    },
    { label: "Follow-ups", value: followUpsLoading ? "Loading…" : String(followUpCount) },
  ];

  const sections: AgentMetaSection[] = [{ items: taskItems }];

  if (intent) {
    sections.push({
      title: "Planning context",
      description: "Mem Agent planning signals. Inferred values are labeled.",
      items: [
        { label: "Type", value: intent.task_type || "—" },
        {
          label: "Priority",
          value: formatIntentValue({
            value: intent.priority,
            provenance: intent.priority_provenance,
          }),
        },
        {
          label: "Ideal completion",
          value: formatIntentValue({
            value: intent.ideal_completion_at
              ? formatAgentDate({ value: intent.ideal_completion_at, includeTime: true })
              : null,
            provenance: intent.ideal_completion_provenance,
          }),
        },
        {
          label: "Deadline",
          value: formatIntentValue({
            value: intent.deadline_at
              ? formatAgentDate({ value: intent.deadline_at, includeTime: true })
              : null,
            provenance: intent.deadline_provenance,
          }),
        },
        {
          label: "Stale after",
          value: formatIntentValue({
            value: intent.staleness_at
              ? formatAgentDate({ value: intent.staleness_at, includeTime: true })
              : null,
            provenance: intent.staleness_provenance,
          }),
        },
        {
          label: "Next wake",
          value: intent.next_wake_at
            ? formatAgentDate({ value: intent.next_wake_at, includeTime: true })
            : "—",
        },
        {
          label: "Remind by",
          value: intent.remind_by_at
            ? formatAgentDate({ value: intent.remind_by_at, includeTime: true })
            : "—",
        },
        {
          label: "Estimated duration",
          value: formatIntentValue({
            value: intent.estimated_duration_minutes
              ? `${intent.estimated_duration_minutes} minutes`
              : null,
            provenance: intent.duration_provenance,
          }),
        },
        {
          label: "Repercussions",
          wide: true,
          value: formatIntentValue({
            value: intent.repercussions,
            provenance: intent.repercussions_provenance,
          }),
        },
        { label: "Outcome", value: intent.terminal_disposition || "—" },
      ],
    });
  }

  return <AgentMetaCard sections={sections} />;
};

const formatIntentValue = ({
  value,
  provenance,
}: {
  value: string | null | undefined;
  provenance: string | null | undefined;
}) => {
  if (!value) return "—";
  if (provenance === "user_set") return `${value} · User set`;
  if (provenance === "agent_inferred") return `${value} · Inferred`;
  return value;
};
